import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './MemberManagement.css';

function MemberManagement({ householdId, isOwner, onMemberChange }) {
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadMembers();
    if (isOwner) {
      loadPendingMembers();
    }
  }, [householdId, isOwner]);

  const loadMembers = async () => {
    try {
      const response = await api.get(`/household/${householdId}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Error loading members:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingMembers = async () => {
    try {
      const response = await api.get(`/household/${householdId}/pending-members`);
      setPendingMembers(response.data.pending_members || []);
    } catch (error) {
      console.error('Error loading pending members:', error);
    }
  };

  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await api.post(`/household/${householdId}/members/${userId}/approve`);
      loadMembers();
      loadPendingMembers();
      if (onMemberChange) onMemberChange();
    } catch (error) {
      console.error('Error approving member:', error);
      alert('Failed to approve member: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeny = async (userId) => {
    setActionLoading(userId);
    try {
      await api.post(`/household/${householdId}/members/${userId}/deny`);
      loadPendingMembers();
    } catch (error) {
      console.error('Error denying member:', error);
      alert('Failed to deny request: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (userId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from this household?`)) {
      return;
    }
    
    setActionLoading(userId);
    try {
      await api.delete(`/household/${householdId}/members/${userId}`);
      loadMembers();
      if (onMemberChange) onMemberChange();
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="loading">Loading members...</div>;
  }

  return (
    <div className="member-management">
      {/* Pending Members Section - Only for owners */}
      {isOwner && pendingMembers.length > 0 && (
        <div className="pending-section">
          <h3>
            <span className="section-icon">⏳</span>
            Pending Requests ({pendingMembers.length})
          </h3>
          <div className="pending-list">
            {pendingMembers.map((member) => (
              <div key={member.user_id} className="pending-card">
                <div className="member-avatar pending">
                  {member.name.charAt(0).toUpperCase()}
                </div>
                <div className="member-info">
                  <div className="member-name">{member.name}</div>
                  <div className="member-email">{member.email}</div>
                  <div className="member-date">Requested: {formatDate(member.requested_at)}</div>
                </div>
                <div className="pending-actions">
                  <button
                    className="approve-btn"
                    onClick={() => handleApprove(member.user_id)}
                    disabled={actionLoading === member.user_id}
                  >
                    {actionLoading === member.user_id ? '...' : '✓ Approve'}
                  </button>
                  <button
                    className="deny-btn"
                    onClick={() => handleDeny(member.user_id)}
                    disabled={actionLoading === member.user_id}
                  >
                    {actionLoading === member.user_id ? '...' : '✗ Deny'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Members Section */}
      <div className="members-section">
        <h3>
          <span className="section-icon">👥</span>
          Members ({members.length})
        </h3>
        <div className="members-list">
          {members.map((member) => (
            <div key={member.user_id} className="member-card">
              <div className={`member-avatar ${member.role}`}>
                {member.name.charAt(0).toUpperCase()}
              </div>
              <div className="member-info">
                <div className="member-name">
                  {member.name}
                  {member.role === 'owner' && <span className="role-badge owner">👑 Owner</span>}
                  {member.role === 'admin' && <span className="role-badge admin">⭐ Admin</span>}
                </div>
                <div className="member-email">{member.email}</div>
                <div className="member-date">Joined: {formatDate(member.joined_at)}</div>
              </div>
              {isOwner && member.role !== 'owner' && (
                <div className="member-actions">
                  <button
                    className="remove-btn"
                    onClick={() => handleRemove(member.user_id, member.name)}
                    disabled={actionLoading === member.user_id}
                    title="Remove member"
                  >
                    {actionLoading === member.user_id ? '...' : '🗑️'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MemberManagement;
